/**
 * VisualSearchV3 - Premium DNA-Based Material Search
 * ===================================================
 * Redesigned visual search page with premium UI + Image Editor.
 * 
 * @author SlabReality Team
 * @version 3.2 - Premium Design + Image Editor
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, Search, Loader2, AlertCircle, ChevronRight, 
  Heart, Sparkles, Palette, Layers, Zap, Thermometer,
  ScanSearch, Dna, Target, Bookmark, ArrowRight, Crop,
  Sliders, Grid, List, ChevronLeft, Maximize2, RefreshCw, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageEditor from '../components/dna/ImageEditor';
import { getMaterialImageUrl, getMaterialThumbnail, handleImageError, PLACEHOLDER_IMAGE } from '../utils/materialImages';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Sanitize image URL helper (uses materialImages utility)
const sanitizeImageUrl = (url) => {
  if (!url) return PLACEHOLDER_IMAGE;
  if (url.startsWith('/api/')) {
    return `${API}${url}`;
  }
  return url;
};

// Color hex mapping
const COLOR_HEX_MAP = {
  beige: '#D4B896', black: '#2A2A2A', blue: '#4A90D9', brown: '#8B6914',
  green: '#4CAF50', grey: '#9E9E9E', orange: '#FF9800', pink: '#E91E63',
  red: '#F44336', white: '#F5F5F5', gold: '#D4AF37', cream: '#FFFDD0',
  teal: '#008080', purple: '#9C27B0'
};

const getColorHex = (name) => COLOR_HEX_MAP[name?.toLowerCase()] || '#666';

// Sample images for quick testing
const SAMPLE_MATERIALS = [
  { id: 'mat_bianco_carrara_23', name: 'Marmo chiaro', image: '/api/images/materials/mat_bianco_carrara_23/texture_21ea32df.jpg' },
  { id: 'mat_nero_belgio_194', name: 'Marmo scuro', image: '/api/images/materials/mat_nero_belgio_194/texture_ed0ae982.jpg' },
  { id: 'mat_amazzonite_3', name: 'Quarzite', image: '/api/images/materials/mat_amazzonite_3/texture_9ae5c9a5.jpg' },
  { id: 'mat_onice_arancio_204', name: 'Onice', image: '/api/images/materials/mat_onice_arancio_204/texture_39efd06d.jpg' },
  { id: 'mat_calacatta_65', name: 'Ambiente cucina', image: '/api/images/materials/mat_calacatta_65/texture_973cfe50.jpg' },
  { id: 'mat_emperador_112', name: 'Bagno', image: '/api/images/materials/mat_emperador_112/texture_4acd84a1.jpg' },
];

// Features data
const FEATURES = [
  { 
    icon: ScanSearch, 
    title: 'Ricerca intelligente', 
    description: 'La nostra AI analizza colori, venature, texture e pattern per risultati precisi.' 
  },
  { 
    icon: Dna, 
    title: 'DNA visivo', 
    description: 'Ogni pietra ha un DNA unico. Troviamo quelle che gli somigliano di più.' 
  },
  { 
    icon: Target, 
    title: 'Risultati accurati', 
    description: 'Materiali selezionati dal nostro database di pietre naturali di alta qualità.' 
  },
  { 
    icon: Bookmark, 
    title: 'Salva e confronta', 
    description: 'Salva i risultati, confronta i materiali e usali nei tuoi progetti.' 
  },
];

// How it works steps
const HOW_IT_WORKS = [
  { 
    num: 1, 
    icon: Upload, 
    title: 'Carica un\'immagine', 
    description: 'Trascina o seleziona una foto di una pietra o di un ambiente.' 
  },
  { 
    num: 2, 
    icon: Dna, 
    title: 'Analisi AI', 
    description: 'La nostra AI analizza il DNA visivo: colori, venature, texture e pattern.' 
  },
  { 
    num: 3, 
    icon: Search, 
    title: 'Trova i match', 
    description: 'Ti mostriamo i materiali più simili presenti nel nostro database.' 
  },
  { 
    num: 4, 
    icon: Layers, 
    title: 'Confronta e scegli', 
    description: 'Confronta le opzioni, salva e provale nel tuo spazio.' 
  },
];

// Match tier badge styles - using Material Identity Engine tiers
const MATCH_TIER_STYLES = {
  exact_match: { bg: 'bg-gradient-to-r from-[#B08B5C] to-[#D4AF37]', text: 'text-black', label: 'Match Esatto', icon: '★' },
  exceptional: { bg: 'bg-gradient-to-r from-emerald-600 to-emerald-500', text: 'text-white', label: 'Eccezionale', icon: '◆' },
  strong: { bg: 'bg-[#B08B5C]/40', text: 'text-[#D4AF37]', label: 'Forte', icon: '●' },
  compatible: { bg: 'bg-[#3a3a3a]', text: 'text-gray-300', label: 'Compatibile', icon: '○' },
  partial: { bg: 'bg-[#2a2a2a]', text: 'text-gray-400', label: 'Parziale', icon: '·' },
  weak: { bg: 'bg-[#1a1a1a]', text: 'text-gray-500', label: 'Debole', icon: '' }
};

// Legacy category styles (for backward compatibility)
const CATEGORY_STYLES = {
  best_match: MATCH_TIER_STYLES.exact_match,
  highly_similar: MATCH_TIER_STYLES.exceptional,
  similar_alternative: MATCH_TIER_STYLES.strong,
  possible_direction: MATCH_TIER_STYLES.compatible,
  other: MATCH_TIER_STYLES.partial
};

const getHumanExplanation = (result) => {
  // Use match_reasons from backend if available
  if (result.match_reasons && result.match_reasons.length > 0) {
    return result.match_reasons.join(' • ');
  }
  
  const score = result.similarity_score || 0;
  const tier = result.match_tier || '';
  
  if (tier === 'exact_match' || score >= 98) return "Stesso materiale identificato — match esatto";
  if (tier === 'exceptional' || score >= 92) return "Somiglianza eccezionale — stesso DNA visivo";
  if (tier === 'strong' || score >= 84) return "Forte compatibilità — caratteristiche simili";
  if (tier === 'compatible' || score >= 72) return "Compatibile — stile e colore simili";
  if (tier === 'partial' || score >= 60) return "Parzialmente simile — esplora le alternative";
  return "Opzione stilistica — potrebbe completare il design";
};

/**
 * DNA Analysis Display - REDESIGNED
 */
const DNAAnalysis = ({ dna }) => {
  if (!dna) return null;
  
  // Map texture type to Italian
  const textureMap = {
    'uniform': 'Uniforme',
    'granular': 'Granulare',
    'veined': 'Venata',
    'marbled': 'Marmorizzata',
    'crystalline': 'Cristallina',
    'layered': 'Stratificata'
  };
  
  // Map color to Italian
  const colorMap = {
    'beige': 'Beige',
    'white': 'Bianco',
    'black': 'Nero',
    'grey': 'Grigio',
    'brown': 'Marrone',
    'green': 'Verde',
    'blue': 'Blu',
    'gold': 'Oro',
    'cream': 'Crema',
    'red': 'Rosso',
    'pink': 'Rosa',
    'orange': 'Arancione'
  };
  
  // Map temperature to Italian
  const tempMap = {
    'warm': 'Calda',
    'cool': 'Fredda',
    'neutral': 'Neutra'
  };
  
  // Support both old and new DNA formats
  // New format: dominant_color, brightness, contrast, saturation, edge_density, texture_complexity, pattern_type
  // Old format: primary_color, brightness_score, contrast_score, etc.
  
  const primaryColor = colorMap[(dna.dominant_color || dna.primary_color)?.toLowerCase()] || dna.dominant_color || dna.primary_color || '-';
  const texture = textureMap[(dna.pattern_type || dna.texture_type)?.toLowerCase()] || dna.pattern_type || dna.texture_type || '-';
  const temperature = dna.saturation > 0.3 ? 'Calda' : (dna.saturation < 0.1 ? 'Fredda' : 'Neutra');
  
  // Generate DNA interpretation
  const getInterpretation = () => {
    const contrast = (dna.contrast || dna.contrast_score || 0) * 100;
    const brightness = (dna.brightness || dna.brightness_score || 0) * 100;
    
    if (contrast > 60 && brightness > 50) {
      return "Materiale con forte contrasto e venature marcate su base chiara. Ideale per superfici protagoniste in ambienti dal look sofisticato e contemporaneo.";
    } else if (contrast > 60) {
      return "Materiale con contrasto deciso e carattere distintivo. Perfetto per creare punti focali in ambienti eleganti.";
    } else if (brightness > 60) {
      return "Materiale luminoso e raffinato. Ideale per ambienti che richiedono luminosità e un'atmosfera accogliente.";
    } else {
      return "Materiale equilibrato con toni naturali. Versatile per molteplici applicazioni architettoniche e di design.";
    }
  };
  
  return (
    <div className="bg-[#121212] rounded-2xl border border-[#1a1a1a] overflow-hidden">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#B08B5C]" />
          Analisi DNA
        </h3>
        
        {/* Main attributes row */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500">Colore Primario</span>
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full border border-[#2a2a2a]"
                style={{ backgroundColor: getColorHex(dna.dominant_color || dna.primary_color) }}
              />
              <span className="text-white font-medium">{primaryColor}</span>
            </div>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500">Texture</span>
            </div>
            <span className="text-white font-medium">{texture}</span>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Thermometer className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500">Temperatura</span>
            </div>
            <span className="text-white font-medium">{temperature}</span>
          </div>
        </div>
        
        {/* Progress bars */}
        <div className="space-y-3 mb-6">
          {[
            { label: 'Contrasto', value: dna.contrast || dna.contrast_score || 0 },
            { label: 'Complessità', value: dna.texture_complexity || dna.complexity_score || 0 },
            { label: 'Luminosità', value: dna.brightness || dna.brightness_score || 0 },
            { label: 'Movimento', value: dna.edge_density || 0 }
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center gap-4">
              <span className="text-sm text-gray-400 w-24">{label}</span>
              <div className="flex-1 h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full"
                  style={{ 
                    width: `${value * 100}%`,
                    background: 'linear-gradient(90deg, #B08B5C 0%, #8B7355 100%)'
                  }}
                />
              </div>
              <span className="text-sm text-gray-400 w-12 text-right">{(value * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
        
        {/* Color Palette */}
        {dna.palette && dna.palette.length > 0 && (
          <div className="mb-6">
            <span className="text-sm text-gray-400 mb-3 block">Palette Colori</span>
            <div className="flex gap-2">
              {dna.palette.slice(0, 6).map((color, i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-2 border-[#2a2a2a]"
                  style={{ backgroundColor: color.hex }}
                  title={color.family}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Interpretation box */}
      <div className="bg-[#1a1a1a] border-t border-[#2a2a2a] p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#B08B5C] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[#B08B5C] font-medium mb-1">Cosa significa</h4>
            <p className="text-gray-400 text-sm leading-relaxed">{getInterpretation()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Result Card - REDESIGNED for horizontal scroll
 */
const ResultCard = ({ result, onView, onSave, onHover, viewMode = 'grid' }) => {
  // Support both old and new API response formats
  const score = result.similarity_score || (result.confidence * 100) || (result.scores?.final * 100) || result.final_score || 0;
  const name = result.material_name || result.name || result.material_id;
  // Use image_url first (R2), then fallbacks
  const imageUrl = sanitizeImageUrl(result.image_url || result.image_main || result.thumbnail_url);
  const stoneType = result.stone_type || 'Pietra';
  const origin = result.origin || 'Italia';
  
  // Use new match_tier system (fallback to score-based)
  const matchTier = result.match_tier || (score >= 98 ? 'exact_match' : score >= 92 ? 'exceptional' : score >= 84 ? 'strong' : score >= 72 ? 'compatible' : score >= 60 ? 'partial' : 'weak');
  const tierStyle = MATCH_TIER_STYLES[matchTier] || MATCH_TIER_STYLES.compatible;
  const tierLabel = result.match_tier_label || tierStyle.label;
  const isExactMatch = result.is_exact_match === true || matchTier === 'exact_match';
  const explanation = getHumanExplanation(result);
  
  // DNA similarity dots (based on score)
  const dnaDots = Math.round((score / 100) * 5);
  
  // Image error handler
  const onImageError = (e) => {
    e.target.onerror = null;
    e.target.src = PLACEHOLDER_IMAGE;
  };
  
  // List view
  if (viewMode === 'list') {
    return (
      <div 
        className="flex items-center gap-4 bg-[#121212] rounded-xl border border-[#1a1a1a] p-4 hover:border-[#B08B5C]/50 transition-all"
        onMouseEnter={() => onHover?.(result, 'enter')}
        onMouseLeave={() => onHover?.(result, 'leave')}
      >
        {/* Image thumbnail */}
        <div className="flex-shrink-0 w-24 h-24 rounded-lg bg-[#0a0a0a] overflow-hidden">
          {imageUrl ? (
            <img src={imageUrl} alt={name} className="w-full h-full object-cover" loading="lazy" onError={onImageError} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-gray-600 text-xs">No image</span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h4 className="text-white font-medium truncate">{name}</h4>
            <span className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${tierStyle.bg} ${tierStyle.text}`}>
              {tierStyle.icon && `${tierStyle.icon} `}{tierLabel} • {score.toFixed(0)}%
            </span>
          </div>
          <p className="text-gray-500 text-sm mb-1">{stoneType} • {origin}</p>
          <p className="text-gray-400 text-xs">{explanation}</p>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 flex-shrink-0">
          <Button 
            variant="outline" 
            size="sm"
            onClick={(e) => { e.stopPropagation(); onSave(result); }}
            className="border-[#2a2a2a] text-gray-400 hover:bg-[#1a1a1a] px-2"
          >
            <Heart className="w-4 h-4" />
          </Button>
          <Button 
            onClick={() => onView(result)}
            className="bg-[#B08B5C] text-black hover:bg-[#C9A46E] text-sm font-medium"
            size="sm"
          >
            Visualizza
          </Button>
        </div>
      </div>
    );
  }
  
  // Grid view (default)
  return (
    <div 
      className="flex-shrink-0 w-[220px] bg-[#121212] rounded-xl border border-[#1a1a1a] overflow-hidden hover:border-[#B08B5C]/50 transition-all group"
      onMouseEnter={() => onHover?.(result, 'enter')}
      onMouseLeave={() => onHover?.(result, 'leave')}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-[#0a0a0a]">
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="w-full h-full object-cover" loading="lazy" onError={onImageError} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-gray-600 text-sm">No image</span>
          </div>
        )}
        
        {/* Match tier badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 rounded text-xs font-bold ${tierStyle.bg} ${tierStyle.text}`}>
          {tierStyle.icon && `${tierStyle.icon} `}{tierLabel}
        </div>
        
        {/* Score badge */}
        <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium">
          {score.toFixed(0)}%
        </div>
        
        {/* Favorite button */}
        <button 
          onClick={(e) => { e.stopPropagation(); onSave(result); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <Heart className="w-4 h-4 text-gray-300" />
        </button>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h4 className="text-white font-medium text-sm mb-1 truncate">{name}</h4>
        <p className="text-gray-500 text-xs mb-2">{stoneType} • {origin}</p>
        
        {/* Match explanation */}
        <p className="text-gray-400 text-xs mb-3 line-clamp-2">{explanation}</p>
        
        {/* DNA Similarity dots */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500">DNA</span>
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full ${i < dnaDots ? 'bg-[#B08B5C]' : 'bg-[#2a2a2a]'}`}
              />
            ))}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            onClick={() => onView(result)}
            className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E] text-sm font-medium"
            size="sm"
          >
            Visualizza
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Visual Search V3 Component
 */
const VisualSearchV3 = () => {
  const navigate = useNavigate();
  
  const [step, setStep] = useState('upload'); // upload, editing, analyzing, results
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dna, setDna] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  
  // Results view state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [sortBy, setSortBy] = useState('score'); // 'score' | 'name' | 'type'
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showImageFullscreen, setShowImageFullscreen] = useState(false);
  const carouselRef = React.useRef(null);
  
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Carica un file immagine valido');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
      // Don't set uploadedImage yet - wait for editor
      setShowEditor(true);
      setStep('editing');
    };
    reader.readAsDataURL(file);
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  });
  
  // Handle edited image from ImageEditor
  const handleEditorSave = (editedImageDataUrl) => {
    const base64 = editedImageDataUrl.split(',')[1];
    setUploadedImage(base64);
    setImagePreview(editedImageDataUrl);
    setShowEditor(false);
    setStep('upload');
    toast.success('Immagine modificata applicata!');
  };
  
  const handleEditorCancel = () => {
    setShowEditor(false);
    setStep('upload');
    // Keep the original image preview
    if (imagePreview) {
      const base64 = imagePreview.split(',')[1];
      setUploadedImage(base64);
    }
  };
  
  const handleSearch = async (imageOverride = null) => {
    const imageToAnalyze = imageOverride || uploadedImage;
    
    if (!imageToAnalyze) {
      toast.error('Carica prima un\'immagine');
      return;
    }
    
    setLoading(true);
    setStep('analyzing');
    
    try {
      // Ensure we send pure base64 (remove data URL prefix if present)
      let base64Data = imageToAnalyze;
      if (base64Data.includes(',')) {
        base64Data = base64Data.split(',')[1];
      }
      
      console.log('[VisualSearch] handleSearch with base64 length:', base64Data?.length);
      
      const response = await axios.post(`${API}/api/visual-search/v2/search`, {
        image_base64: base64Data,
        top_k: 10
      });
      
      console.log('[VisualSearch] handleSearch response:', response.data);
      
      // Handle new lightweight visual search response format
      if (response.data.results) {
        // Transform results to ensure consistent format
        const transformedResults = (response.data.results || []).map(r => ({
          ...r,
          similarity_score: r.similarity_score || (r.confidence * 100) || 0,
          image_main: r.image_url || r.image_main,
          thumbnail_url: r.image_url || r.thumbnail_url
        }));
        
        console.log('[VisualSearch] Transformed results:', transformedResults.length);
        
        setDna(response.data.analysis?.features || response.data.analysis || {});
        setMatches(transformedResults);
        setStep('results');
      } else if (response.data.success) {
        setDna(response.data.dna);
        setMatches(response.data.matches || []);
        setStep('results');
      } else {
        throw new Error(response.data.error || 'Ricerca fallita');
      }
      
    } catch (err) {
      console.error('[VisualSearch] Search error:', err);
      toast.error('Analisi fallita. Riprova.');
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };
  
  // Load sample image for quick test
  const loadSampleImage = async (sample) => {
    setLoading(true);
    setStep('analyzing');
    
    try {
      console.log('[VisualSearch] Loading sample:', sample.image);
      const response = await fetch(`${API}${sample.image}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }
      
      const blob = await response.blob();
      const reader = new FileReader();
      
      reader.onload = async () => {
        const dataUrl = reader.result; // Full data URL: data:image/jpeg;base64,...
        setImagePreview(dataUrl);
        
        // Extract pure base64 (remove data URL prefix)
        const base64Only = dataUrl.split(',')[1];
        setUploadedImage(base64Only);
        
        console.log('[VisualSearch] Calling API with base64 length:', base64Only?.length);
        
        // Call search API
        try {
          const searchResponse = await axios.post(`${API}/api/visual-search/v2/search`, {
            image_base64: base64Only,
            top_k: 10
          });
          
          console.log('[VisualSearch] API Response:', searchResponse.data);
          
          if (searchResponse.data.results) {
            // Transform results to ensure consistent format
            const transformedResults = (searchResponse.data.results || []).map(r => ({
              ...r,
              similarity_score: r.similarity_score || (r.confidence * 100) || 0,
              image_main: r.image_url || r.image_main,
              thumbnail_url: r.image_url || r.thumbnail_url
            }));
            
            console.log('[VisualSearch] Transformed results:', transformedResults.length);
            
            setDna(searchResponse.data.analysis?.features || searchResponse.data.analysis || {});
            setMatches(transformedResults);
            setStep('results');
          } else {
            throw new Error(searchResponse.data.error || 'No results returned');
          }
        } catch (apiErr) {
          console.error('[VisualSearch] API error:', apiErr);
          toast.error('Ricerca fallita. Riprova.');
          setStep('upload');
        }
        
        setLoading(false);
      };
      
      reader.onerror = () => {
        console.error('[VisualSearch] FileReader error');
        toast.error('Errore nella lettura dell\'immagine');
        setLoading(false);
        setStep('upload');
      };
      
      reader.readAsDataURL(blob);
      
    } catch (err) {
      console.error('[VisualSearch] Load sample error:', err);
      toast.error('Errore nel caricamento dell\'esempio');
      setLoading(false);
      setStep('upload');
    }
  };
  
  // Open editor for current image
  const openEditor = () => {
    if (imagePreview) {
      setShowEditor(true);
      setStep('editing');
    }
  };
  
  const handleView = (result) => {
    navigate(`/materials/${result.material_id}`);
  };
  
  const handleSave = (result) => {
    toast.info('Funzione salvataggio in arrivo!');
  };
  
  const hoverTimers = React.useRef({});
  const handleHover = (result, action) => {
    if (action === 'enter') {
      hoverTimers.current[result.material_id] = setTimeout(() => {
        axios.post(`${API}/api/match/track`, {
          input_material_id: 'uploaded_image',
          suggested_material_id: result.material_id,
          similarity_score: result.final_score,
          user_action: 'hover_material',
          context: 'visual_search_v3'
        }).catch(() => {});
      }, 1000);
    } else {
      if (hoverTimers.current[result.material_id]) {
        clearTimeout(hoverTimers.current[result.material_id]);
        delete hoverTimers.current[result.material_id];
      }
    }
  };
  
  const resetSearch = () => {
    setStep('upload');
    setUploadedImage(null);
    setImagePreview(null);
    setDna(null);
    setMatches([]);
    setShowEditor(false);
    setViewMode('grid');
    setSortBy('score');
  };
  
  // Helper to get score from different API response formats
  const getMatchScore = (m) => m.similarity_score || (m.confidence * 100) || (m.scores?.final * 100) || m.final_score || 0;
  
  // Sort function for matches
  const sortMatches = (matchList) => {
    return [...matchList].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.material_name || a.name || a.material_id || '').localeCompare(b.material_name || b.name || b.material_id || '');
        case 'type':
          return (a.stone_type || '').localeCompare(b.stone_type || '');
        case 'score':
        default:
          return getMatchScore(b) - getMatchScore(a);
      }
    });
  };
  
  const filteredMatches = sortMatches(
    matches.filter(m => getMatchScore(m) >= 20)
  );
  
  // Carousel scroll handlers
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 240 * 3; // 3 cards width
      carouselRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  // Upload/Landing View
  if (step === 'upload' || step === 'analyzing' || step === 'editing') {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        
        <main className="relative">
          {/* Image Editor Modal */}
          {showEditor && imagePreview && (
            <div className="fixed inset-0 z-50 bg-black/95 flex items-start justify-center overflow-y-auto py-8">
              <ImageEditor
                imageSrc={imagePreview}
                onSave={handleEditorSave}
                onCancel={handleEditorCancel}
              />
            </div>
          )}
          
          {/* Hero Section with Decorative Images */}
          <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden pt-20">
            {/* Decorative stone images - Left side */}
            <div className="absolute left-0 top-10 w-80 opacity-70 hidden lg:block">
              <img 
                src={`${API}/api/images/materials/mat_bianco_carrara_23/texture_21ea32df.jpg`}
                alt="" 
                className="w-72 h-72 object-cover rounded-2xl shadow-2xl transform -rotate-12 translate-x-[-20%] translate-y-[10%]"
              />
              <img 
                src={`${API}/api/images/materials/mat_calacatta_65/texture_973cfe50.jpg`}
                alt="" 
                className="w-56 h-80 object-cover rounded-2xl shadow-2xl transform rotate-6 translate-x-[10%] translate-y-[-30%]"
              />
            </div>
            
            {/* Decorative stone images - Right side */}
            <div className="absolute right-0 top-10 w-80 opacity-70 hidden lg:block">
              <img 
                src={`${API}/api/images/materials/mat_emperador_112/texture_4acd84a1.jpg`}
                alt="" 
                className="w-72 h-96 object-cover rounded-2xl shadow-2xl transform rotate-12 translate-x-[20%] translate-y-[5%]"
              />
              <img 
                src={`${API}/api/images/materials/mat_amazzonite_3/texture_9ae5c9a5.jpg`}
                alt="" 
                className="w-56 h-64 object-cover rounded-2xl shadow-2xl transform -rotate-6 translate-x-[30%] translate-y-[20%]"
              />
            </div>
            
            <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-[#B08B5C]">AI</span>
                <span className="text-white"> Visual Search</span>
              </h1>
              
              {/* Subtitle */}
              <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-12">
                Carica un'immagine di una pietra o di un ambiente.
                <br />
                La nostra AI analizza il DNA visivo e trova i materiali più simili.
              </p>
              
              {/* Upload Area */}
              {step === 'analyzing' ? (
                <div className="bg-[#1a1a1a] rounded-2xl p-12 border border-[#B08B5C]/30 max-w-2xl mx-auto">
                  <Loader2 className="w-16 h-16 mx-auto text-[#B08B5C] animate-spin mb-6" />
                  <h3 className="text-xl font-semibold text-white mb-2">Analisi in corso</h3>
                  <p className="text-gray-400">Estrazione DNA visivo e ricerca materiali...</p>
                </div>
              ) : (
                <>
                  <div
                    {...getRootProps()}
                    className={`
                      bg-[#1a1a1a]/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 
                      border-2 border-dashed transition-all duration-300 cursor-pointer
                      max-w-2xl mx-auto
                      ${isDragActive 
                        ? 'border-[#B08B5C] bg-[#B08B5C]/10' 
                        : 'border-[#B08B5C]/50 hover:border-[#B08B5C] hover:bg-[#1a1a1a]'}
                    `}
                  >
                    <input {...getInputProps()} />
                    
                    {imagePreview && uploadedImage ? (
                      <div className="space-y-4">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-48 mx-auto rounded-xl border border-[#2a2a2a]"
                        />
                        <p className="text-gray-400 text-sm">Clicca o trascina per cambiare immagine</p>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditor();
                            }}
                            variant="outline"
                            size="lg"
                            className="border-[#B08B5C]/50 text-[#B08B5C] hover:bg-[#B08B5C]/20"
                          >
                            <Crop className="w-5 h-5 mr-2" />
                            Modifica / Ritaglia
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSearch();
                            }}
                            size="lg"
                            className="bg-[#B08B5C] text-black hover:bg-[#C9A46E] font-semibold px-8"
                          >
                            <Search className="w-5 h-5 mr-2" />
                            Trova Materiali Simili
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="w-16 h-16 mx-auto rounded-full border-2 border-[#B08B5C]/50 flex items-center justify-center">
                          <Upload className="w-8 h-8 text-[#B08B5C]" />
                        </div>
                        <div>
                          <p className="text-white font-medium text-lg">
                            {isDragActive ? 'Rilascia qui l\'immagine' : 'Trascina e rilascia un\'immagine qui'}
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            oppure clicca per selezionare
                          </p>
                        </div>
                        <p className="text-gray-500 text-xs">
                          JPG, PNG, WebP • Max 10MB
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Sample Images */}
                  <div className="mt-12 max-w-3xl mx-auto">
                    <div className="flex items-center justify-center gap-4 mb-6">
                      <div className="h-px flex-1 bg-[#2a2a2a]" />
                      <span className="text-gray-500 text-sm">Oppure prova con un'immagine di esempio</span>
                      <div className="h-px flex-1 bg-[#2a2a2a]" />
                    </div>
                    
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {SAMPLE_MATERIALS.map((sample) => (
                        <button
                          key={sample.id}
                          onClick={() => loadSampleImage(sample)}
                          className="group relative aspect-square rounded-lg overflow-hidden border border-[#1a1a1a] hover:border-[#B08B5C] transition-all"
                        >
                          <img 
                            src={`${API}${sample.image}`}
                            alt={sample.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-2">
                            <span className="text-white text-xs font-medium">{sample.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
          
          {/* Features Section */}
          <section className="py-16 border-t border-[#1a1a1a]">
            <div className="max-w-6xl mx-auto px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {FEATURES.map((feature, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#B08B5C]/10 flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-[#B08B5C]" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          {/* How it Works Section */}
          <section className="py-16 bg-[#111111]">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-3xl font-bold text-white text-center mb-12">Come funziona</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {HOW_IT_WORKS.map((step, idx) => (
                  <div key={idx} className="relative">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-gray-400" />
                      </div>
                      <div className="pt-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-6 h-6 rounded-full bg-[#B08B5C] text-black text-sm font-bold flex items-center justify-center">
                            {step.num}
                          </span>
                          <h3 className="text-white font-semibold">{step.title}</h3>
                        </div>
                        <p className="text-gray-400 text-sm">{step.description}</p>
                      </div>
                    </div>
                    
                    {/* Connector arrow */}
                    {idx < HOW_IT_WORKS.length - 1 && (
                      <div className="hidden md:block absolute top-7 -right-3 text-gray-600">
                        <ArrowRight className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    );
  }
  
  // Results View - REDESIGNED
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      {/* Fullscreen Image Modal */}
      {showImageFullscreen && imagePreview && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-8"
          onClick={() => setShowImageFullscreen(false)}
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-[#B08B5C]"
            onClick={() => setShowImageFullscreen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={imagePreview} 
            alt="Fullscreen" 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Results Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <Sparkles className="w-7 h-7 text-[#B08B5C]" />
                Risultati Ricerca
              </h1>
              <p className="text-gray-400">
                Abbiamo trovato {filteredMatches.length} materiali simili alla tua immagine.
              </p>
            </div>
            <Button 
              onClick={resetSearch} 
              variant="outline" 
              className="border-[#2a2a2a] text-gray-300 hover:bg-[#1a1a1a]"
            >
              <Search className="w-4 h-4 mr-2" />
              Nuova Ricerca
            </Button>
          </div>
          
          {/* Top Section: Image + DNA Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            {/* Your Image Card */}
            <div className="bg-[#121212] rounded-2xl border border-[#1a1a1a] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-[#1a1a1a]">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium">La tua immagine</h3>
                  <span className="text-gray-500 text-sm">IMG_upload.jpg</span>
                </div>
                <button 
                  onClick={() => setShowImageFullscreen(true)}
                  className="w-8 h-8 rounded-lg bg-[#1a1a1a] flex items-center justify-center hover:bg-[#2a2a2a] transition-colors"
                  title="Visualizza a schermo intero"
                >
                  <Maximize2 className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <div className="p-4">
                {imagePreview && (
                  <img 
                    src={imagePreview} 
                    alt="Uploaded" 
                    className="w-full max-h-[350px] object-contain rounded-xl cursor-pointer"
                    onClick={() => setShowImageFullscreen(true)}
                  />
                )}
              </div>
            </div>
            
            {/* DNA Analysis Card */}
            <DNAAnalysis dna={dna} />
          </div>
          
          {/* Results Section */}
          <div className="mb-8">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-white">Migliori abbinamenti</h2>
                <span className="px-3 py-1 rounded-full bg-[#1a1a1a] text-gray-400 text-sm">
                  {filteredMatches.length} risultati
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                {/* View Toggle */}
                <div className="flex bg-[#1a1a1a] rounded-lg p-1">
                  <button 
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[#B08B5C] text-black' : 'text-gray-400 hover:text-white'}`}
                    title="Vista griglia"
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-[#B08B5C] text-black' : 'text-gray-400 hover:text-white'}`}
                    title="Vista lista"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Sort Dropdown */}
                <div className="relative">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>Ordina per:</span>
                    <button 
                      onClick={() => setShowSortMenu(!showSortMenu)}
                      className="flex items-center gap-1 text-white hover:text-[#B08B5C] transition-colors"
                    >
                      {sortBy === 'score' && 'Migliore corrispondenza'}
                      {sortBy === 'name' && 'Nome'}
                      {sortBy === 'type' && 'Tipo materiale'}
                      <ChevronRight className={`w-4 h-4 transition-transform ${showSortMenu ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                  
                  {/* Sort Dropdown Menu */}
                  {showSortMenu && (
                    <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a] shadow-xl z-10 min-w-[200px]">
                      {[
                        { value: 'score', label: 'Migliore corrispondenza' },
                        { value: 'name', label: 'Nome A-Z' },
                        { value: 'type', label: 'Tipo materiale' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => { setSortBy(option.value); setShowSortMenu(false); }}
                          className={`w-full px-4 py-3 text-left text-sm transition-colors ${
                            sortBy === option.value 
                              ? 'text-[#B08B5C] bg-[#B08B5C]/10' 
                              : 'text-gray-300 hover:bg-[#2a2a2a]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* No Results */}
            {filteredMatches.length === 0 && (
              <div className="text-center py-12 bg-[#121212] rounded-2xl border border-[#1a1a1a]">
                <AlertCircle className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Nessun match trovato</h3>
                <p className="text-gray-400">Prova a caricare un'immagine diversa</p>
              </div>
            )}
            
            {/* Results - Grid or List View */}
            {filteredMatches.length > 0 && viewMode === 'list' && (
              <div className="space-y-3">
                {filteredMatches.map(result => (
                  <ResultCard 
                    key={result.material_id} 
                    result={result} 
                    onView={handleView} 
                    onSave={handleSave} 
                    onHover={handleHover}
                    viewMode="list"
                  />
                ))}
              </div>
            )}
            
            {/* Horizontal Scrollable Results (Grid View) */}
            {filteredMatches.length > 0 && viewMode === 'grid' && (
              <div className="relative">
                {/* Left scroll button */}
                <button 
                  onClick={() => scrollCarousel('left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#1a1a1a]/90 backdrop-blur flex items-center justify-center hover:bg-[#2a2a2a] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                
                <div 
                  ref={carouselRef}
                  className="flex gap-4 overflow-x-auto pb-4 px-8 scrollbar-hide" 
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {filteredMatches.map(result => (
                    <ResultCard 
                      key={result.material_id} 
                      result={result} 
                      onView={handleView} 
                      onSave={handleSave} 
                      onHover={handleHover}
                      viewMode="grid"
                    />
                  ))}
                </div>
                
                {/* Right scroll button */}
                {filteredMatches.length > 4 && (
                  <button 
                    onClick={() => scrollCarousel('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#1a1a1a]/90 backdrop-blur flex items-center justify-center hover:bg-[#2a2a2a] transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Not found what you're looking for */}
          <div className="bg-[#121212] rounded-2xl border border-[#1a1a1a] p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                  <Search className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Non hai trovato quello che cercavi?</h3>
                  <p className="text-gray-400 text-sm">
                    Ritaglia meglio l'immagine per analizzare solo la lastra di pietra.
                  </p>
                </div>
              </div>
              <Button 
                onClick={openEditor}
                className="bg-[#B08B5C] text-black hover:bg-[#C9A46E] font-medium"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Modifica immagine
              </Button>
            </div>
          </div>
          
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default VisualSearchV3;
